/**
 * ClassificationBins - Drop zones for specimen classification
 * Four bins: Living/Non-Living, Unicellular/Multicellular
 * Supports both drag-drop and click-to-classify
 */

import Phaser from 'phaser';
import { Classification } from '@/types';
import type { ClassificationSystem, ClassificationResultEvent } from '@/game/systems';

export interface ClassificationBinsConfig {
  /** Position X (center) */
  x?: number;
  /** Position Y */
  y?: number;
  /** Bin size */
  binSize?: number;
  /** Spacing between bins */
  spacing?: number;
}

interface ClassificationBin {
  container: Phaser.GameObjects.Container;
  bg: Phaser.GameObjects.Graphics;
  label: Phaser.GameObjects.Text;
  icon: Phaser.GameObjects.Text;
  classification: Classification;
  isHovered: boolean;
}

/**
 * ClassificationBins provides the UI for classifying specimens
 */
export class ClassificationBins {
  private scene: Phaser.Scene;
  private classificationSystem: ClassificationSystem;
  private config: Required<ClassificationBinsConfig>;

  /** Main container */
  private container!: Phaser.GameObjects.Container;

  /** Classification bins */
  private bins: ClassificationBin[] = [];

  /** Classify button */
  private classifyButtonContainer!: Phaser.GameObjects.Container;
  private classifyButtonBg!: Phaser.GameObjects.Graphics;
  private classifyButtonText!: Phaser.GameObjects.Text;

  /** Selected classification */
  private selectedClassification: Classification | null = null;

  /** Status text */
  private statusText!: Phaser.GameObjects.Text;

  /** Feedback text */
  private feedbackText!: Phaser.GameObjects.Text;

  /** Colors */
  private static readonly COLORS = {
    panelBg: 0x0a1628,
    panelBorder: 0x1e3a5f,
    binBg: 0x1a365d,
    binBgHover: 0x2d4a6f,
    binBgSelected: 0x2b6cb0,
    living: 0x48bb78,
    nonLiving: 0x718096,
    unicellular: 0x9f7aea,
    multicellular: 0xed8936,
    textPrimary: 0xe2e8f0,
    textSecondary: 0x90cdf4,
    buttonEnabled: 0x4299e1,
    buttonDisabled: 0x4a5568,
    correctFeedback: 0x68d391,
    incorrectFeedback: 0xfc8181,
    accent: 0x4299e1,
  };

  /** Bin configurations */
  private static readonly BIN_CONFIGS: Array<{
    classification: Classification;
    label: string;
    icon: string;
    color: number;
    row: number;
    col: number;
  }> = [
    { classification: Classification.LIVING, label: 'LIVING', icon: '🌱', color: 0x48bb78, row: 0, col: 0 },
    { classification: Classification.NON_LIVING, label: 'NON-LIVING', icon: '🪨', color: 0x718096, row: 0, col: 1 },
    { classification: Classification.UNICELLULAR, label: 'UNICELLULAR', icon: '🦠', color: 0x9f7aea, row: 1, col: 0 },
    { classification: Classification.MULTICELLULAR, label: 'MULTICELLULAR', icon: '🌿', color: 0xed8936, row: 1, col: 1 },
  ];

  constructor(
    scene: Phaser.Scene,
    classificationSystem: ClassificationSystem,
    config: ClassificationBinsConfig = {}
  ) {
    this.scene = scene;
    this.classificationSystem = classificationSystem;

    const camera = scene.cameras.main;
    this.config = {
      x: config.x ?? camera.width / 2,
      y: config.y ?? camera.height - 120,
      binSize: config.binSize ?? 100,
      spacing: config.spacing ?? 15,
    };

    this.create();
    this.setupEventListeners();
    this.updateState();
  }

  /**
   * Create all UI elements
   */
  private create(): void {
    this.container = this.scene.add.container(this.config.x, this.config.y);
    this.container.setDepth(1000);

    this.createBins();
    this.createClassifyButton();
    this.createStatusText();
    this.createFeedbackText();
  }

  /**
   * Create the four classification bins
   */
  private createBins(): void {
    const { binSize, spacing } = this.config;
    const totalWidth = (binSize * 2) + spacing;
    const startX = -totalWidth / 2 + binSize / 2;
    const rowHeight = binSize + spacing;

    for (const binConfig of ClassificationBins.BIN_CONFIGS) {
      const x = startX + binConfig.col * (binSize + spacing);
      const y = -rowHeight + binConfig.row * rowHeight;

      const bin = this.createBin(
        binConfig.classification,
        binConfig.label,
        binConfig.icon,
        binConfig.color,
        x,
        y
      );

      this.bins.push(bin);
    }
  }

  /**
   * Create a single classification bin
   */
  private createBin(
    classification: Classification,
    label: string,
    icon: string,
    color: number,
    x: number,
    y: number
  ): ClassificationBin {
    const { binSize } = this.config;
    const container = this.scene.add.container(x, y);

    // Background
    const bg = this.scene.add.graphics();
    this.drawBinBg(bg, binSize, color, false, false);

    // Icon
    const iconText = this.scene.add.text(0, -10, icon, {
      fontFamily: 'system-ui, -apple-system, sans-serif',
      fontSize: '28px',
    });
    iconText.setOrigin(0.5);

    // Label
    const labelText = this.scene.add.text(0, 25, label, {
      fontFamily: 'system-ui, -apple-system, sans-serif',
      fontSize: '11px',
      color: '#e2e8f0',
      fontStyle: 'bold',
    });
    labelText.setOrigin(0.5);

    container.add([bg, iconText, labelText]);
    container.setSize(binSize, binSize);
    container.setInteractive({ useHandCursor: true, dropZone: true });

    // Interaction handlers
    container.on('pointerover', () => {
      if (this.selectedClassification !== classification) {
        this.drawBinBg(bg, binSize, color, true, false);
      }
    });

    container.on('pointerout', () => {
      if (this.selectedClassification !== classification) {
        this.drawBinBg(bg, binSize, color, false, false);
      }
    });

    container.on('pointerdown', () => {
      this.selectClassification(classification);
    });

    // Drop zone for drag-drop
    container.on('drop', (_pointer: Phaser.Input.Pointer, _gameObject: Phaser.GameObjects.GameObject) => {
      this.attemptClassification(classification);
    });

    this.container.add(container);

    return {
      container,
      bg,
      label: labelText,
      icon: iconText,
      classification,
      isHovered: false,
    };
  }

  /**
   * Draw bin background
   */
  private drawBinBg(
    graphics: Phaser.GameObjects.Graphics,
    size: number,
    color: number,
    hover: boolean,
    selected: boolean
  ): void {
    graphics.clear();

    const bgColor = selected
      ? ClassificationBins.COLORS.binBgSelected
      : hover
        ? ClassificationBins.COLORS.binBgHover
        : ClassificationBins.COLORS.binBg;

    // Shadow
    graphics.fillStyle(0x000000, 0.2);
    graphics.fillRoundedRect(-size / 2 + 3, -size / 2 + 3, size, size, 10);

    // Background
    graphics.fillStyle(bgColor, 0.9);
    graphics.fillRoundedRect(-size / 2, -size / 2, size, size, 10);

    // Border
    const borderColor = selected ? color : ClassificationBins.COLORS.panelBorder;
    const borderAlpha = selected ? 1 : 0.8;
    graphics.lineStyle(selected ? 3 : 2, borderColor, borderAlpha);
    graphics.strokeRoundedRect(-size / 2, -size / 2, size, size, 10);

    if (selected) {
      // Glow effect
      graphics.lineStyle(1, color, 0.5);
      graphics.strokeRoundedRect(-size / 2 - 2, -size / 2 - 2, size + 4, size + 4, 12);
    }
  }

  /**
   * Create the classify button
   */
  private createClassifyButton(): void {
    const buttonWidth = 150;
    const buttonHeight = 40;
    const y = 70;

    this.classifyButtonContainer = this.scene.add.container(0, y);

    // Background
    this.classifyButtonBg = this.scene.add.graphics();
    this.drawButtonBg(false);

    // Text
    this.classifyButtonText = this.scene.add.text(0, 0, 'CLASSIFY', {
      fontFamily: 'system-ui, -apple-system, sans-serif',
      fontSize: '14px',
      color: '#e2e8f0',
      fontStyle: 'bold',
    });
    this.classifyButtonText.setOrigin(0.5);

    this.classifyButtonContainer.add([this.classifyButtonBg, this.classifyButtonText]);
    this.classifyButtonContainer.setSize(buttonWidth, buttonHeight);
    this.classifyButtonContainer.setInteractive({ useHandCursor: true });

    this.classifyButtonContainer.on('pointerdown', () => {
      if (this.classificationSystem.canClassify() && this.selectedClassification) {
        this.attemptClassification(this.selectedClassification);
      }
    });

    this.container.add(this.classifyButtonContainer);
  }

  /**
   * Draw button background
   */
  private drawButtonBg(enabled: boolean): void {
    const width = 150;
    const height = 40;

    this.classifyButtonBg.clear();

    const bgColor = enabled
      ? ClassificationBins.COLORS.buttonEnabled
      : ClassificationBins.COLORS.buttonDisabled;

    // Shadow
    this.classifyButtonBg.fillStyle(0x000000, 0.2);
    this.classifyButtonBg.fillRoundedRect(-width / 2 + 2, -height / 2 + 2, width, height, 8);

    // Background
    this.classifyButtonBg.fillStyle(bgColor, enabled ? 1 : 0.5);
    this.classifyButtonBg.fillRoundedRect(-width / 2, -height / 2, width, height, 8);

    // Border
    this.classifyButtonBg.lineStyle(2, enabled ? ClassificationBins.COLORS.accent : 0x4a5568, enabled ? 1 : 0.5);
    this.classifyButtonBg.strokeRoundedRect(-width / 2, -height / 2, width, height, 8);
  }

  /**
   * Create status text
   */
  private createStatusText(): void {
    this.statusText = this.scene.add.text(0, 35, 'Collect evidence to classify', {
      fontFamily: 'system-ui, -apple-system, sans-serif',
      fontSize: '11px',
      color: '#90cdf4',
    });
    this.statusText.setOrigin(0.5);

    this.container.add(this.statusText);
  }

  /**
   * Create feedback text
   */
  private createFeedbackText(): void {
    this.feedbackText = this.scene.add.text(0, 120, '', {
      fontFamily: 'system-ui, -apple-system, sans-serif',
      fontSize: '13px',
      color: '#68d391',
      wordWrap: { width: 300 },
      align: 'center',
    });
    this.feedbackText.setOrigin(0.5);
    this.feedbackText.setVisible(false);

    this.container.add(this.feedbackText);
  }

  /**
   * Set up event listeners
   */
  private setupEventListeners(): void {
    this.scene.events.on('classification:state', this.handleStateChange, this);
    this.scene.events.on('classification:correct', this.handleCorrect, this);
    this.scene.events.on('classification:incorrect', this.handleIncorrect, this);
    this.scene.events.on('classification:reset', this.handleReset, this);
  }

  /**
   * Handle state change
   */
  private handleStateChange(): void {
    this.updateState();
  }

  /**
   * Handle correct classification
   */
  private handleCorrect(event: ClassificationResultEvent): void {
    this.showFeedback(event.result.feedback, true);
    this.animateCorrect();
  }

  /**
   * Handle incorrect classification
   */
  private handleIncorrect(event: ClassificationResultEvent): void {
    this.showFeedback(event.result.feedback, false);
    this.animateIncorrect();
  }

  /**
   * Handle reset
   */
  private handleReset(): void {
    this.selectedClassification = null;
    this.feedbackText.setVisible(false);
    this.updateState();
    this.updateBinSelection();
  }

  /**
   * Select a classification
   */
  private selectClassification(classification: Classification): void {
    this.selectedClassification = classification;
    this.updateBinSelection();
    this.updateState();

    // Emit selection event
    this.scene.events.emit('classification:selected', classification);
  }

  /**
   * Update bin visual selection
   */
  private updateBinSelection(): void {
    const { binSize } = this.config;

    for (const bin of this.bins) {
      const isSelected = bin.classification === this.selectedClassification;
      const binConfig = ClassificationBins.BIN_CONFIGS.find(c => c.classification === bin.classification);
      const color = binConfig?.color ?? ClassificationBins.COLORS.binBg;

      this.drawBinBg(bin.bg, binSize, color, false, isSelected);
    }
  }

  /**
   * Attempt to classify with selected classification
   */
  private attemptClassification(classification: Classification): void {
    const specimen = this.classificationSystem.getCurrentSpecimen();
    if (!specimen) {
      this.showFeedback('No specimen to classify. Scan a specimen first.', false);
      return;
    }

    if (!this.classificationSystem.canClassify()) {
      const minRequired = this.classificationSystem.getMinEvidenceRequired();
      const collected = this.classificationSystem.getCollectedEvidence().length;
      this.showFeedback(`Need ${minRequired - collected} more evidence to classify.`, false);
      return;
    }

    this.classificationSystem.classify(specimen, classification);
  }

  /**
   * Update button and status based on state
   */
  private updateState(): void {
    const canClassify = this.classificationSystem.canClassify();
    const evidenceCount = this.classificationSystem.getCollectedEvidence().length;
    const minRequired = this.classificationSystem.getMinEvidenceRequired();

    // Update button
    const buttonEnabled = canClassify && this.selectedClassification !== null;
    this.drawButtonBg(buttonEnabled);
    this.classifyButtonText.setAlpha(buttonEnabled ? 1 : 0.5);

    // Update status text
    if (!canClassify) {
      this.statusText.setText(`Collect ${minRequired - evidenceCount} more evidence`);
      this.statusText.setColor('#fc8181');
    } else if (!this.selectedClassification) {
      this.statusText.setText('Select a classification bin');
      this.statusText.setColor('#90cdf4');
    } else {
      this.statusText.setText('Ready to classify!');
      this.statusText.setColor('#68d391');
    }
  }

  /**
   * Show feedback message
   */
  private showFeedback(message: string, correct: boolean): void {
    this.feedbackText.setText(message);
    this.feedbackText.setColor(
      correct
        ? `#${ClassificationBins.COLORS.correctFeedback.toString(16)}`
        : `#${ClassificationBins.COLORS.incorrectFeedback.toString(16)}`
    );
    this.feedbackText.setVisible(true);

    // Fade out after delay
    this.scene.time.delayedCall(4000, () => {
      this.scene.tweens.add({
        targets: this.feedbackText,
        alpha: 0,
        duration: 500,
        onComplete: () => {
          this.feedbackText.setAlpha(1);
          this.feedbackText.setVisible(false);
        },
      });
    });
  }

  /**
   * Animate correct feedback
   */
  private animateCorrect(): void {
    // Flash the selected bin green
    if (this.selectedClassification) {
      const bin = this.bins.find(b => b.classification === this.selectedClassification);
      if (bin) {
        const flash = this.scene.add.graphics();
        flash.fillStyle(ClassificationBins.COLORS.correctFeedback, 0.5);
        flash.fillRoundedRect(-this.config.binSize / 2, -this.config.binSize / 2, this.config.binSize, this.config.binSize, 10);

        bin.container.add(flash);
        bin.container.sendToBack(flash);

        this.scene.tweens.add({
          targets: flash,
          alpha: 0,
          duration: 800,
          onComplete: () => flash.destroy(),
        });
      }
    }

    // Pulse the button
    this.scene.tweens.add({
      targets: this.classifyButtonContainer,
      scaleX: 1.1,
      scaleY: 1.1,
      duration: 150,
      yoyo: true,
    });
  }

  /**
   * Animate incorrect feedback
   */
  private animateIncorrect(): void {
    // Shake the selected bin
    if (this.selectedClassification) {
      const bin = this.bins.find(b => b.classification === this.selectedClassification);
      if (bin) {
        const originalX = bin.container.x;

        this.scene.tweens.add({
          targets: bin.container,
          x: originalX - 5,
          duration: 50,
          yoyo: true,
          repeat: 3,
          onComplete: () => {
            bin.container.setX(originalX);
          },
        });

        // Flash red
        const flash = this.scene.add.graphics();
        flash.fillStyle(ClassificationBins.COLORS.incorrectFeedback, 0.3);
        flash.fillRoundedRect(-this.config.binSize / 2, -this.config.binSize / 2, this.config.binSize, this.config.binSize, 10);

        bin.container.add(flash);
        bin.container.sendToBack(flash);

        this.scene.tweens.add({
          targets: flash,
          alpha: 0,
          duration: 500,
          onComplete: () => flash.destroy(),
        });
      }
    }
  }

  /**
   * Set visibility
   */
  public setVisible(visible: boolean): void {
    this.container.setVisible(visible);
  }

  /**
   * Set position
   */
  public setPosition(x: number, y: number): void {
    this.container.setPosition(x, y);
  }

  /**
   * Get the container
   */
  public getContainer(): Phaser.GameObjects.Container {
    return this.container;
  }

  /**
   * Get selected classification
   */
  public getSelectedClassification(): Classification | null {
    return this.selectedClassification;
  }

  /**
   * Clear selection
   */
  public clearSelection(): void {
    this.selectedClassification = null;
    this.updateBinSelection();
    this.updateState();
  }

  /**
   * Clean up
   */
  public destroy(): void {
    this.scene.events.off('classification:state', this.handleStateChange, this);
    this.scene.events.off('classification:correct', this.handleCorrect, this);
    this.scene.events.off('classification:incorrect', this.handleIncorrect, this);
    this.scene.events.off('classification:reset', this.handleReset, this);

    this.container.destroy();
  }
}
